/**
 * Reading a date the way somebody actually types one.
 *
 * ---------------------------------------------------------------------------
 * WHY THIS EXISTS
 *
 * Every date filter in this app was a native `<input type="date">`, and a
 * native date input will not accept a two-digit year. Measured in Chromium,
 * not assumed:
 *
 *     typed "92326"     -> "0026-09-23"      <-- the year 26 AD
 *     typed "092326"    -> "0026-09-23"
 *     typed "1526"      -> ""                <-- nothing at all
 *     typed "9232026"   -> "2026-09-23"      <-- only the full year works
 *
 * So typing the date the way a person says it out loud produces a VALID ISO
 * DATE nineteen centuries in the past. It passes the browser's validation, it
 * passes FastAPI's `dt.date` parsing, the query runs, and the table comes back
 * empty. Nothing anywhere says the date was misread. Sarah, via Jonathan,
 * 2026-09-24: *"the dates for chart of accounts search isn't working"* --
 * and Jonathan: *"you need to enter the dates exactly. that is annoying. it
 * should work with simple entries like 92326 is auto 09/23/26."*
 *
 * ⚠️ THE SILENT PART IS THE BUG. A date field that refused "92326" would only
 * be annoying. One that accepts it, means something else by it, and returns an
 * empty ledger is a wrong answer delivered confidently -- which is the failure
 * this codebase keeps saying it exists to stop.
 * ---------------------------------------------------------------------------
 *
 * ⚠️ MONTH FIRST, ALWAYS. This is a US company reading US bank feeds and a US
 * QuickBooks file, and `9/5` here means September 5th. There is no setting and
 * there should not be one: a date format that changes under you is worse than
 * one that is merely wrong for somebody, because the same string then means two
 * things depending on state nobody can see.
 */

/** The canonical form everything else speaks: `YYYY-MM-DD`. */
export type ISODate = string;

/**
 * Which century a two-digit year belongs to.
 *
 * 00-68 -> 2000s, 69-99 -> 1900s. This is the POSIX/`strptime` window and it is
 * chosen rather than invented: it is what every other tool this office pastes
 * dates between already does, so "26" means the same year here as it does in
 * Excel. The books in question start in the 2010s, so the 1900s half is nearly
 * dead weight -- but "69" silently becoming 2069 in an accounting app is a
 * worse failure than it becoming 1969, which at least looks wrong on sight.
 */
function century(twoDigit: number): number {
  return twoDigit <= 68 ? 2000 + twoDigit : 1900 + twoDigit;
}

/**
 * Is this a real date? `new Date(y, m, d)` rolls over silently -- February 30th
 * becomes March 2nd -- so the only reliable check is to build it and see
 * whether the parts came back unchanged.
 *
 * ⚠️ Rolling over is exactly the class of bug this whole file is about, so it
 * must not be reintroduced by the validator. "Feb 30" is a typo and has to be
 * REFUSED, not helpfully reinterpreted as a date the person did not type.
 */
function real(year: number, month: number, day: number): boolean {
  if (month < 1 || month > 12 || day < 1 || day > 31) return false;
  const d = new Date(year, month - 1, day);
  return (
    d.getFullYear() === year && d.getMonth() === month - 1 && d.getDate() === day
  );
}

function iso(year: number, month: number, day: number): ISODate {
  return `${String(year).padStart(4, "0")}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
}

/**
 * Turn whatever was typed into `YYYY-MM-DD`, or `null` if it is not a date.
 *
 * Accepted, with `2026-09-23` as the example throughout:
 *
 *   * `2026-09-23`   ISO, passed straight through -- it is what the URL and the
 *                    API carry, so a pasted link's date must survive a round
 *                    trip through this function untouched
 *   * `9/23/26`      slashes, dots or dashes, one- or two-digit month and day
 *   * `9-23-2026`
 *   * `92326`        five to eight bare digits: M/D/YY, MM/DD/YY, M/D/YYYY ...
 *   * `09232026`
 *   * `20260923`     eight digits STARTING WITH A YEAR -- see the note below
 *   * `9/23`         no year at all, meaning this year
 *   * `923`          three or four bare digits, same
 *
 * ⚠️ Eight digits are ambiguous and the tie is broken DELIBERATELY. `20260923`
 * could be 2026-09-23 or the 20th of February 6093. Anything whose first four
 * digits look like a plausible year (1900-2099) is read as ISO; everything else
 * is read as MMDDYYYY. That means `10201999` is October 20th 1999 rather than
 * the 1st of February in the year 1020 -- the reading a person meant, and the
 * only one of the two that is ever typed on purpose.
 *
 * ⚠️ Returns `null` rather than throwing, and `null` rather than guessing. A
 * caller showing "not a date" is the whole point; a caller receiving a date
 * this function was not sure about is the bug being fixed.
 */
export function parseLooseDate(
  input: string,
  today: Date = new Date(),
): ISODate | null {
  const text = input.trim();
  if (!text) return null;

  // ISO first, and unmodified. A URL carries `since=2026-09-23`, so this is the
  // round trip that has to be exact.
  const isoMatch = /^(\d{4})-(\d{1,2})-(\d{1,2})$/.exec(text);
  if (isoMatch) {
    const [, y, m, d] = isoMatch;
    const year = Number(y), month = Number(m), day = Number(d);
    return real(year, month, day) ? iso(year, month, day) : null;
  }

  // Separated: 9/23/26, 9-23-2026, 9.23, 9/23
  const parts = /^(\d{1,2})[/.\-](\d{1,2})(?:[/.\-](\d{1,4}))?$/.exec(text);
  if (parts) {
    const month = Number(parts[1]);
    const day = Number(parts[2]);
    const year = yearFrom(parts[3], today);
    if (year === null) return null;
    return real(year, month, day) ? iso(year, month, day) : null;
  }

  // Bare digits.
  if (/^\d+$/.test(text)) return fromDigits(text, today);

  return null;
}

/**
 * The year part of a separated date. Absent means this year -- `9/23` typed in
 * September 2026 means this September, which is what somebody filtering a
 * ledger they are looking at right now means by it.
 */
function yearFrom(raw: string | undefined, today: Date): number | null {
  if (raw === undefined) return today.getFullYear();
  if (raw.length === 1 || raw.length === 3) return null; // 9/23/6 is a typo
  if (raw.length === 2) return century(Number(raw));
  return Number(raw);
}

/**
 * A run of digits with no separators at all -- the form this whole change is
 * about, because it is what gets typed on a number pad.
 *
 * ⚠️ Length is the ONLY signal available here, so each length gets exactly one
 * reading and lengths with no sensible reading are refused rather than
 * stretched to fit. Three digits is `MDD`, not `MMD`: nobody types a leading
 * zero on the month and then drops one off the day.
 */
function fromDigits(digits: string, today: Date): ISODate | null {
  const year = today.getFullYear();
  let m: number, d: number, y: number;

  switch (digits.length) {
    case 3: // 923 -> 9/23, this year
      m = Number(digits.slice(0, 1));
      d = Number(digits.slice(1, 3));
      y = year;
      break;
    case 4: // 0923 -> 09/23, this year
      m = Number(digits.slice(0, 2));
      d = Number(digits.slice(2, 4));
      y = year;
      break;
    case 5: // 92326 -> 9/23/26   ⚠️ the one from the complaint
      m = Number(digits.slice(0, 1));
      d = Number(digits.slice(1, 3));
      y = century(Number(digits.slice(3, 5)));
      break;
    case 6: // 092326 -> 09/23/26
      m = Number(digits.slice(0, 2));
      d = Number(digits.slice(2, 4));
      y = century(Number(digits.slice(4, 6)));
      break;
    case 7: // 9232026 -> 9/23/2026
      m = Number(digits.slice(0, 1));
      d = Number(digits.slice(1, 3));
      y = Number(digits.slice(3, 7));
      break;
    case 8: {
      // The ambiguous one. See the docstring: a leading plausible year wins.
      const lead = Number(digits.slice(0, 4));
      if (lead >= 1900 && lead <= 2099) {
        y = lead;
        m = Number(digits.slice(4, 6));
        d = Number(digits.slice(6, 8));
      } else {
        m = Number(digits.slice(0, 2));
        d = Number(digits.slice(2, 4));
        y = Number(digits.slice(4, 8));
      }
      break;
    }
    default:
      // 1, 2 and 9+ digits. A bare "9" is not a date, and refusing it is what
      // lets the field say so instead of filtering a ledger to the 9th of
      // some month it picked.
      return null;
  }

  return real(y, m, d) ? iso(y, m, d) : null;
}

/**
 * `2026-09-23` -> `09/23/2026`, for showing a parsed date back to the person.
 *
 * Deliberately NOT `toLocaleDateString`: these strings are plain calendar dates
 * with no time and no zone, and handing `new Date("2026-09-23")` to the
 * formatter parses it as UTC MIDNIGHT and then renders it in local time, which
 * is the previous day for everybody west of Greenwich. That is the same class
 * of off-by-one `todayISO` in `age.ts` carries a warning about.
 */
export function formatUSDate(value: ISODate | null | undefined): string {
  if (!value) return "";
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!m) return value;
  return `${m[2]}/${m[3]}/${m[1]}`;
}
